import { useAppDispatch, useAppSelector } from '~/store';
import { selectHasFeature } from '~/organizations/selectors';
import { PostEntity } from '~/post/PostEntity';
import { campaignTab } from '~/routes';
import { useParams } from 'react-router-dom';
import { PublishingPost } from '~/post/types.generated';
import { RpcUpdate } from '~/post/types';
import { CampaignDetails, CampaignTab } from '../types';

const getTabToRedirectTo = (post: PublishingPost | RpcUpdate) => {
  const isDraft = PostEntity.isDraft(post);
  const isSent = PostEntity.isSent(post);
  const needsApproval = PostEntity.needsApproval(post);
  if (!isDraft && !isSent) return CampaignTab.Scheduled;
  if (!isDraft && isSent) return CampaignTab.Sent;
  if (isDraft && !needsApproval) return CampaignTab.Drafts;
  if (isDraft && needsApproval) return CampaignTab.Approvals;
};

type useCampaignTagReturn =
  | {
      campaignDetails: CampaignDetails;
      onCampaignTagClick: () => void;
    }
  | {
      campaignDetails: undefined;
      onCampaignTagClick: undefined;
    };

export const useCampaignTag = (
  post: PublishingPost | RpcUpdate
): useCampaignTagReturn => {
  const dispatch = useAppDispatch();
  const { tab: isOnCampaignPage } = useParams() as { tab?: CampaignTab };

  // Check entitlements
  const hasCampaignsFeature = useAppSelector((state) =>
    selectHasFeature(state, 'hasCampaignsFeature')
  );

  // Figure out where to redirect to
  const tabToRedictTo = getTabToRedirectTo(post);

  // Determine whether a campaign tag should be rendered or not
  const campaignDetails: CampaignDetails = PostEntity.getCampaign(post);
  const shouldRenderCampaignTag =
    hasCampaignsFeature && !!campaignDetails && !isOnCampaignPage;

  const onCampaignTagClick = () => {
    dispatch(
      campaignTab.goTo({
        campaignId: campaignDetails.id,
        tab: tabToRedictTo,
      })
    );
  };

  return shouldRenderCampaignTag
    ? {
        campaignDetails,
        onCampaignTagClick,
      }
    : {
        campaignDetails: undefined,
        onCampaignTagClick: undefined,
      };
};
